// apps/web/src/features/admin/form-config/hooks/useFormConfig.ts
import { useEffect, useState } from 'react';
import { useFormConfigStore } from '../state/store';
import { formConfigApi, type JobTypeVersion } from '../api/client';

interface UseFormConfigResult {
  loading: boolean;
  error: string | null;
  versions: JobTypeVersion[];
  autoForkIfPublished: () => Promise<void>;
}

export function useFormConfig(jobTypeId: string): UseFormConfigResult {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [versions, setVersions] = useState<JobTypeVersion[]>([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const [published, list] = await Promise.all([
          formConfigApi.getCurrentPublished(jobTypeId),
          formConfigApi.listVersions(jobTypeId),
        ]);
        if (cancelled) return;
        setVersions(list);
        const draft = list.find((v) => v.state === 'draft');
        if (draft) {
          const full = await formConfigApi.getVersion(draft.id);
          if (cancelled) return;
          useFormConfigStore.getState().loadVersion(full);
        } else if (published) {
          useFormConfigStore.getState().loadVersion(published);
        } else {
          setError('No published version found for this form');
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [jobTypeId]);

  async function autoForkIfPublished(): Promise<void> {
    const s = useFormConfigStore.getState();
    if (!s.version || s.version.state !== 'published') return;
    const draft = await formConfigApi.createDraft(jobTypeId);
    // F-040 — createDraft is IDEMPOTENT: if a draft already exists (another
    // tab/surface forked first) it returns THAT draft at its CURRENT revision.
    // Adopting it while keeping this tab's published-based content would arm
    // the next autosave with a fresh baseRevision and wholesale-clobber the
    // other surface's writes — the surviving F-032 vector (found live, V6).
    // contentRevision > 0 = someone already wrote to it → adopt the identity
    // (so the banner can render) but flip conflicted, which suspends autosave;
    // the only way forward is the banner's Reload. A pristine draft (rev 0) is
    // a snapshot of published and safe to overwrite with this tab's edit.
    useFormConfigStore.getState().adoptDraft(draft);
    if (draft.contentRevision > 0) {
      useFormConfigStore.getState().markConflicted();
    }
    setVersions((v) => [draft, ...v.filter((x) => x.id !== draft.id)]);
  }

  return { loading, error, versions, autoForkIfPublished };
}
